import { useState, type FormEvent } from "react";
import { api, type Project } from "../api";
import { useProjects } from "../project-context";

function Copy({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="copy"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      }}
    >
      {done ? "✓ kopiert" : "kopier"}
    </button>
  );
}

function snippet(publicKey: string): string {
  const origin = window.location.origin;
  return `import { init } from "@betaworks/insight";

init({
  key: "${publicKey}",
  endpoint: "${origin}",
});`;
}

function ProjectCard({ project, onChange }: { project: Project; onChange: () => void }) {
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function revealSecret() {
    const full = await api.project(project.id);
    setSecret(full.secret_key ?? null);
  }
  async function rotate() {
    if (!confirm("Roter nøkler? Eksisterende integrasjoner slutter å virke til de oppdateres.")) return;
    setBusy(true);
    try {
      const keys = await api.rotateKeys(project.id);
      setSecret(keys.secret_key);
      onChange();
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (!confirm(`Slette «${project.name}» og alle data? Kan ikke angres.`)) return;
    setBusy(true);
    try {
      await api.deleteProject(project.id);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card reveal">
      <h3 className="card-title">
        {project.name}
        <span className="mono" style={{ fontSize: 11, color: "var(--faint)" }}>{project.role}</span>
      </h3>

      <div className="field">
        <label>Public key — trygg i klient</label>
        <div className="keyline">
          <code className="code">{project.public_key}</code>
          <Copy text={project.public_key} />
        </div>
      </div>

      <div className="field">
        <label>Secret key — kun server-til-server</label>
        <div className="keyline">
          <code className="code">{secret ?? "•••••••••••••••••••••••••••"}</code>
          {secret ? <Copy text={secret} /> : (
            <button className="copy" onClick={revealSecret}>vis</button>
          )}
        </div>
      </div>

      <div className="field">
        <label>Kom i gang</label>
        <pre className="snippet">{snippet(project.public_key)}</pre>
      </div>

      <div className="row">
        <button className="btn btn-ghost" disabled={busy} onClick={rotate}>Roter nøkler</button>
        <span className="spacer" />
        <button className="btn btn-ghost" disabled={busy} onClick={remove} style={{ color: "var(--coral)" }}>
          Slett
        </button>
      </div>
    </div>
  );
}

export function Projects() {
  const { projects, loading, reload } = useProjects();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setCreating(true);
    try {
      await api.createProject(name.trim());
      setName("");
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kunne ikke opprette");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">Administrasjon</span>
          <h1 className="page-title">Prosjekter</h1>
        </div>
      </div>

      <div className="card reveal" style={{ marginBottom: 16 }}>
        <h3 className="card-title">Nytt prosjekt</h3>
        {err && <div className="error-msg">{err}</div>}
        <form className="row" onSubmit={create} style={{ alignItems: "flex-end" }}>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="pname">Navn</label>
            <input
              id="pname"
              className="input"
              placeholder="f.eks. Betaworks Web"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <button className="btn btn-primary" disabled={creating || !name.trim()}>
            {creating ? "Oppretter …" : "Opprett + generer nøkler"}
          </button>
        </form>
      </div>

      {loading ? (
        <div className="spin" />
      ) : (
        <div className="grid grid-2">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} onChange={reload} />
          ))}
        </div>
      )}
    </div>
  );
}
